import { Shot, useShotListStore } from '@/features/shotlist/store/useShotListStore'
import { Badge } from '@/shared/components/ui/badge'
import { cn } from '@/shared/lib/utils'
import { CheckCircle2, ChevronRight, CircleDashed, Film, ListVideo, RefreshCw, X } from 'lucide-react'

import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { Button } from '@/shared/components/ui/button'
import { ScrollArea } from '@/shared/components/ui/scroll-area'
import { useNavigate } from 'react-router-dom'
import { useStoryboardQuery } from '../queries/storyboard.queries'

interface ShotListPreviewPanelProps {
    sceneId: string
    onClose: () => void
    className?: string
    onShotClick?: (shot: Shot) => void
    selectedShotId?: string | null
}

export const ShotListPreviewPanel = ({ sceneId, onClose, className, onShotClick, selectedShotId }: ShotListPreviewPanelProps) => {
    const { project } = useWorkspace()
    const { shotLists, loadShotList } = useShotListStore()
    const navigate = useNavigate()
    // const { storyboards } = useStoryboardStore() // Removed

    const { data: frames = [] } = useStoryboardQuery(project?.id || '', sceneId)

    const shots = shotLists[sceneId] || []

    const isShotGenerated = (shotId: string) => {
        // Robust 1:1 mapping using the dedicated shotId field
        return frames.some(f => f.shotId === shotId)
    }

    return (
        <div className={cn("border-l flex flex-col h-full bg-background/50 backdrop-blur-sm overflow-hidden transition-all duration-300", className)}>
            <div className="p-4 border-b flex justify-between items-center bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 flex-none sticky top-0 z-10">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                    <ListVideo className="h-4 w-4 text-primary" />
                    Shot List
                    <Badge variant="secondary" className="ml-1 text-[10px] h-5 px-1.5">{shots.length}</Badge>
                </h3>
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 hover:bg-muted"
                        onClick={() => loadShotList(sceneId)}
                        title="Refresh Shot List"
                    >
                        <RefreshCw className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-muted" onClick={onClose}>
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <ScrollArea className="flex-1 w-full">
                {shots.length > 0 ? (
                    <div className="flex flex-col">
                        {shots.map((shot, index) => {
                            const generated = isShotGenerated(shot.id)
                            const isSelected = selectedShotId === shot.id

                            return (
                                <div
                                    key={shot.id}
                                    className={cn(
                                        "group flex items-start gap-3 p-3 border-b border-border/40 cursor-pointer transition-all hover:bg-muted/50",
                                        isSelected && "bg-primary/5 border-l-2 border-l-primary border-b-transparent"
                                    )}
                                    onClick={() => onShotClick?.(shot)}
                                >
                                    {/* Number & Status */}
                                    <div className="flex flex-col items-center gap-1 pt-0.5 min-w-[24px]">
                                        <span className={cn(
                                            "text-xs font-mono font-medium",
                                            isSelected ? "text-primary" : "text-muted-foreground"
                                        )}>
                                            {index + 1}
                                        </span>
                                        {generated ? (
                                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                                        ) : (
                                            <CircleDashed className="h-3 w-3 text-muted-foreground/30 group-hover:text-muted-foreground/50" />
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0 space-y-1.5">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                <Badge variant={isSelected ? "default" : "outline"} className="text-[10px] px-1.5 py-0 h-5 font-bold uppercase tracking-wider">
                                                    {shot.shotType}
                                                </Badge>
                                                {shot.duration && (
                                                    <span className="text-[10px] text-muted-foreground font-mono">
                                                        {shot.duration}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <p className={cn(
                                            "text-sm line-clamp-2 leading-snug",
                                            isSelected ? "text-foreground font-medium" : "text-muted-foreground"
                                        )}>
                                            {shot.description}
                                        </p>

                                        {/* Metadata Tags */}
                                        <div className="flex flex-wrap gap-1.5 pt-0.5">
                                            {shot.cameraAngle && (
                                                <span className="text-[10px] text-muted-foreground/70 bg-muted/50 px-1 rounded flex items-center">
                                                    {shot.cameraAngle}
                                                </span>
                                            )}
                                            {shot.cameraMovement && (
                                                <span className="text-[10px] text-muted-foreground/70 bg-muted/50 px-1 rounded flex items-center">
                                                    {shot.cameraMovement}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Chevron / Active Indicator */}
                                    <div className="flex items-center self-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <ChevronRight className={cn("h-4 w-4", isSelected ? "text-primary" : "text-muted-foreground")} />
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-40 text-center space-y-4 mt-10 p-6">
                        <div className="h-16 w-16 bg-muted/30 rounded-full flex items-center justify-center">
                            <Film className="h-8 w-8 text-muted-foreground/40" />
                        </div>
                        <div className="space-y-1">
                            <p className="text-sm font-medium text-foreground">
                                No shots yet
                            </p>
                            <p className="text-xs text-muted-foreground max-w-[150px] mx-auto leading-relaxed">
                                Switch to the <strong>Shot List</strong> tab to plan your shots for this scene.
                            </p>
                            <Button
                                variant="outline"
                                size="sm"
                                className="mt-4 gap-2 w-full max-w-[150px]"
                                onClick={() => {
                                    if (project?.id) {
                                        navigate(`/workspace/${project.id}/shotlist`)
                                    }
                                }}
                            >
                                <ListVideo className="h-4 w-4" />
                                Go to Shot List
                            </Button>
                        </div>
                    </div>
                )}
            </ScrollArea>
        </div>
    )
}
